import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx-js-style';
import axios from 'axios';

// ─── Format numbers ────────────────────────────────────────────────────────────
const f = (n, dec = 2) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const clr = (n) => n < 0 ? 'text-red-400' : n > 0 ? 'text-emerald-400' : 'text-slate-300';

// ─── Table Row Components ──────────────────────────────────────────────────────
const SectionRow = ({ label, cols }) => (
  <tr>
    <td colSpan={cols + 1} className="px-4 py-1.5 text-xs font-black uppercase tracking-widest text-indigo-300 bg-indigo-500/10 border-t border-b border-indigo-500/20">
      {label}
    </td>
  </tr>
);

const TotalRow = ({ label, values, color = 'text-teal-300', bg = 'bg-teal-500/10' }) => (
  <tr className={`${bg} border-t border-b border-teal-500/20`}>
    <td className={`px-4 py-2 text-xs font-black uppercase tracking-wider ${color} sticky left-0 z-10 bg-slate-950`}>{label}</td>
    {values.map((v, i) => (
      <td key={i} className={`px-3 py-2 text-right font-black text-xs font-mono whitespace-nowrap ${typeof v === 'number' ? (v < 0 ? 'text-red-400' : color) : color}`}>
        {typeof v === 'number' ? f(v) : v}
      </td>
    ))}
  </tr>
);

const DataRow = ({ label, values, sub = false, fmt: fmtFn }) => {
  const fn = fmtFn || f;
  return (
    <tr className="border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors">
      <td className={`px-4 py-1.5 text-xs whitespace-nowrap sticky left-0 z-10 bg-slate-950 ${sub ? 'pl-8 text-slate-400 font-normal' : 'text-slate-300 font-medium'}`}>
        {label}
      </td>
      {values.map((v, i) => (
        <td key={i} className={`px-3 py-1.5 text-right text-xs font-mono whitespace-nowrap ${typeof v === 'number' ? clr(v) : 'text-slate-300'}`}>
          {typeof v === 'number' ? fn(v) : v}
        </td>
      ))}
    </tr>
  );
};

const ProfitRow = ({ label, values }) => (
  <tr className="bg-emerald-500/8 border-t border-b border-emerald-500/20">
    <td className="px-4 py-2 text-xs font-black uppercase tracking-wider text-emerald-300 sticky left-0 z-10 bg-slate-950">{label}</td>
    {values.map((v, i) => (
      <td key={i} className={`px-3 py-2 text-right font-black text-xs font-mono whitespace-nowrap ${v < 0 ? 'text-red-400' : 'text-emerald-300'}`}>
        {f(v)}
      </td>
    ))}
  </tr>
);

// ─── MAIN CALCULATION ENGINE ───────────────────────────────────────────────────
// growthRateMap: optional object keyed by FY year string e.g. { 'FY 2028-29': 20, 'FY 2029-30': 15 }.
// Falls back to cfg.growthPct for years not in map.
function runCalc(rawCfg, tree, sectionTotal, growthRateMap) {
  const cfg = {};
  for (const key in rawCfg) {
    cfg[key] = Number(rawCfg[key]) || 0;
  }

  const N = Math.min(Math.max(cfg.numYears, 1), 30);
  const sYr = cfg.startYear;
  const defaultGr = cfg.growthPct / 100;

  const fy = Array.from({ length: N }, (_, y) => {
    const yr = sYr + y;
    return `FY ${yr}-${String(yr + 1).slice(-2)}`;
  });

  const getGr = (y) => {
    const fyKey = fy[y]; // e.g. 'FY 2028-29'
    if (growthRateMap && growthRateMap[fyKey] !== undefined) {
      return growthRateMap[fyKey] / 100;
    }
    return defaultGr;
  };

  const fyPerc = fy.map((yLabel, idx) => {
    if (growthRateMap && growthRateMap[yLabel] !== undefined) return growthRateMap[yLabel];
    return cfg.growthPct;
  });

  // Extract Dynamic Items from tree
  const otherTemplates = {};

  if (tree && sectionTotal) {
    tree.forEach(main => {
      const mainName = (main.mainName || '').toUpperCase();

      main.categories.forEach(cat => {
        cat.subCategories.forEach(sub => {
          const rawAmt = sectionTotal(sub.subId, sub.subName);
          if (rawAmt === 0) return;

          // Convert absolute rupees to Lakhs to match template
          const amt = rawAmt / 100000;

          if (!otherTemplates[mainName]) {
            otherTemplates[mainName] = [];
          }
          otherTemplates[mainName].push({
            category: cat.catName || 'Items',
            name: sub.subName,
            yr1: amt
          });
        });
      });
    });
  }

  // Projection helper: Year 1 = base, subsequent years = previous * (1 + rate_for_that_year)
  const proj = (base) => {
    const arr = [base];
    for (let y = 1; y < N; y++) arr.push(arr[y - 1] * (1 + getGr(y)));
    return arr;
  };

  const dynamicIncome = [];
  const dynamicExp = [];

  if (otherTemplates['INCOME AND EXPENDITURE']) {
    otherTemplates['INCOME AND EXPENDITURE'].forEach(item => {
      if ((item.category || '').toUpperCase().includes('INCOME')) {
        dynamicIncome.push({ name: item.name, values: proj(item.yr1) });
      } else {
        dynamicExp.push({ name: item.name, values: proj(item.yr1) });
      }
    });
  }

  const dynamicOtherTabs = Object.keys(otherTemplates).map(tmplName => {
    const items = otherTemplates[tmplName];
    const categories = {};
    items.forEach(item => {
      if (!categories[item.category]) categories[item.category] = [];
      categories[item.category].push({
        name: item.name,
        values: proj(item.yr1)
      });
    });
    return {
      id: `dyn_${tmplName.replace(/\s+/g, '_').toLowerCase()}`,
      label: tmplName,
      categories
    };
  });

  const rev = Array(N).fill(0);
  dynamicIncome.forEach(item => {
    item.values.forEach((v, i) => rev[i] += v);
  });

  const totDirectExp = Array(N).fill(0);
  dynamicExp.forEach(item => {
    item.values.forEach((v, i) => totDirectExp[i] += v);
  });

  // Depreciation (Reducing Balance)
  const bWdv = [cfg.bldCost], bD = [cfg.bldCost * cfg.bldDepnPct / 100], bCwdv = [cfg.bldCost - cfg.bldCost * cfg.bldDepnPct / 100];
  const eWdv = [cfg.eqCost], eD = [cfg.eqCost * cfg.eqDepnPct / 100], eCwdv = [cfg.eqCost - cfg.eqCost * cfg.eqDepnPct / 100];
  const cWdv = [cfg.cmpCost], cD = [cfg.cmpCost * cfg.cmpDepnPct / 100], cCwdv = [cfg.cmpCost - cfg.cmpCost * cfg.cmpDepnPct / 100];
  for (let y = 1; y < N; y++) {
    bWdv.push(bCwdv[y - 1]); bD.push(Math.max(0, bWdv[y] * cfg.bldDepnPct / 100)); bCwdv.push(Math.max(0, bWdv[y] - bD[y]));
    eWdv.push(eCwdv[y - 1]); eD.push(Math.max(0, eWdv[y] * cfg.eqDepnPct / 100)); eCwdv.push(Math.max(0, eWdv[y] - eD[y]));
    cWdv.push(cCwdv[y - 1]); cD.push(Math.max(0, cWdv[y] * cfg.cmpDepnPct / 100)); cCwdv.push(Math.max(0, cWdv[y] - cD[y]));
  }
  const totD = bD.map((b, y) => b + eD[y] + cD[y]);


  // Loan
  const annRep = cfg.tlAmt / cfg.repYrs;
  const tlOp = [cfg.tlAmt], tlRep = [0], tlCl = [cfg.tlAmt];
  for (let y = 1; y < N; y++) {
    tlOp.push(tlCl[y - 1]);
    const rep = (y >= cfg.morYrs && tlOp[y] > 0) ? Math.min(annRep, tlOp[y]) : 0;
    tlRep.push(rep);
    tlCl.push(Math.max(0, tlOp[y] - rep));
  }
  const intPay = tlOp.map(t => t * cfg.intRatePct / 100 + cfg.odAmt * cfg.intRatePct / 100);

  // Total Expenditure = Dynamic Exp + Depreciation + Interest
  const totExp = totDirectExp.map((exp, y) => exp + totD[y] + intPay[y]);

  const opProfit = rev.map((r, y) => r - totExp[y]);
  const ebitda = opProfit.map((p, y) => p + intPay[y] + totD[y]);

  // Mgmt & Tax
  const mgmt = [cfg.mgmt1];
  for (let y = 1; y < N; y++) mgmt.push(mgmt[y - 1] * (1 + cfg.mgmtGrowPct / 100));
  const taxInc = opProfit.map((p, y) => Math.max(0, p - mgmt[y]));
  const tax = taxInc.map(t => t * cfg.taxPct / 100);
  const surp = taxInc.map(t => t * (1 - cfg.taxPct / 100));
  const opRes = [0], clRes = [surp[0]];
  for (let y = 1; y < N; y++) { opRes.push(clRes[y - 1]); clRes.push(opRes[y] + surp[y]); }

  // Balance Sheet - Simplified dynamic logic
  const invAdd = [0, 500, 600, 650, 750, 800, 900, 1150, 1150, 1300, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const inv = [0];
  for (let y = 1; y < N; y++) inv.push(inv[y - 1] + (invAdd[y] || 0));
  const recv = rev.map(r => r * 0.01);
  const dep = [120]; for (let y = 1; y < N; y++) dep.push(dep[y - 1] * 1.1);
  const cred = totDirectExp.map(p => Math.max(0, p) * 0.026);
  const prov = tax.slice();
  const totLiab = clRes.map((r, y) => cfg.capAmt + r + Math.max(0, tlCl[y]) + cfg.odAmt + cred[y] + prov[y]);
  const fixedNet = bCwdv.map((b, y) => b + eCwdv[y] + cCwdv[y]);
  const cash = totLiab.map((tl, y) => tl - fixedNet[y] - inv[y] - cfg.leaseAdv - recv[y] - dep[y]);

  // Cash Flow
  const cfSC = Array(N).fill(0); cfSC[0] = cfg.capAmt;
  const cfTL = Array(N).fill(0); cfTL[0] = cfg.tlAmt;
  const cfOD = Array(N).fill(0); cfOD[0] = cfg.odAmt;
  const cfIn = cfSC.map((s, y) => s + cfTL[y] + cfOD[y] + surp[y] + totD[y]);
  const cfCx = Array(N).fill(0); cfCx[0] = cfg.bldCost + cfg.eqCost + cfg.cmpCost + cfg.furnCost;
  const cfDep = Array(N).fill(0); cfDep[0] = cfg.leaseAdv;
  const cfInv = Array(N).fill(0); for (let y = 1; y < N; y++) cfInv[y] = invAdd[y] || 0;
  const cfCA = Array(N).fill(0);
  for (let y = 0; y < N; y++) {
    const ca = recv[y] + dep[y] + cfg.leaseAdv;
    const pca = y === 0 ? 0 : recv[y - 1] + dep[y - 1] + cfg.leaseAdv;
    cfCA[y] = Math.max(0, ca - pca);
  }
  const cfOut = cfCx.map((c, y) => c + cfDep[y] + cfInv[y] + cfCA[y] + tlRep[y]);
  const netCF = cfIn.map((i, y) => i - cfOut[y]);
  const opCash = [0]; for (let y = 1; y < N; y++) opCash.push(Math.max(0, opCash[y - 1] + netCF[y - 1]));
  const clCash = netCF.map((n, y) => opCash[y] + n);

  // DSCR
  const tca = surp.map((s, y) => s + totD[y] + tlRep[y] + intPay[y]);
  const tob = tlRep.map((r, y) => r + intPay[y]);
  const dscr = tca.map((t, y) => tob[y] > 0 ? t / tob[y] : 0);
  const avgDscr = dscr.slice(0, Math.min(5, N)).reduce((a, b) => a + b, 0) / Math.min(5, N);

  return {
    N, fy, fyPerc, rev, totDirectExp,
    dynamicIncome, dynamicExp, dynamicOtherTabs,
    totD, bD, eD, cD, bWdv, bCwdv, eWdv, eCwdv, cWdv, cCwdv,
    intPay, totExp, opProfit, ebitda, mgmt, tax, surp, opRes, clRes,
    tlOp, tlRep, tlCl, inv, recv, dep, cred, prov, totLiab, fixedNet, cash,
    cfSC, cfTL, cfOD, cfIn, cfCx, cfDep, cfInv, cfCA, cfOut, netCF, opCash, clCash,
    tca, tob, dscr, avgDscr
  };
}

// ─── TABLE WRAPPER ─────────────────────────────────────────────────────────────
const TableWrap = ({ title, children }) => (
  <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-xl mb-6">
    <div className="bg-slate-900/60 border-b border-slate-800 px-5 py-3 flex items-center gap-3">
      <div className="w-1 h-5 rounded-full bg-gradient-to-b from-teal-400 to-indigo-500" />
      <span className="text-sm font-bold text-white">{title}</span>
      <span className="text-xs text-slate-500 ml-1">₹ in Lakhs</span>
    </div>
    <div className="overflow-x-auto">
      <table id="export-table" className="w-full border-collapse text-xs">
        {children}
      </table>
    </div>
  </div>
);

const THead = ({ fy, fyPerc, N }) => (
  <thead>
    <tr className="bg-slate-900 border-b border-slate-700">
      <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-400 uppercase tracking-wider sticky left-0 z-20 bg-slate-900 min-w-[200px]">
        Particulars
      </th>
      {fy.map((y, i) => (
        <th key={i} className={`px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wider whitespace-nowrap ${i === 0 || i === N - 1 ? 'text-teal-400' : 'text-slate-400'}`}>
          <div>{y}</div>
          {fyPerc && fyPerc[i] !== undefined && (
            <div className="text-[10px] text-teal-500/70 mt-0.5 tracking-tighter leading-none">{fyPerc[i]}%</div>
          )}
        </th>
      ))}
    </tr>
  </thead>
);

// ─── INPUT PANEL HELPERS ────────────────────────────────────────────────────────
const inputClass = "w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs font-mono text-right outline-none focus:border-teal-500/60 transition";

const SecHeader = ({ id, icon, label, openSec, tog }) => (
  <button onClick={() => tog(id)}
    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-900/40 hover:bg-slate-900/70 border-b border-slate-800 transition text-left">
    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
      <span>{icon}</span> {label}
    </span>
    <span className={`text-slate-500 text-xs transition-transform ${openSec[id] ? 'rotate-180' : ''}`}>▼</span>
  </button>
);

const Field = ({ label, k, step = '1', suffix = '', cfg, set }) => (
  <div className="flex flex-col gap-1 mb-2">
    <label className="text-xs text-slate-500">{label}</label>
    <div className="relative">
      <input type="number" value={cfg[k] === undefined ? '' : cfg[k]} step={step}
        onChange={e => set(k)(e.target.value)}
        className={inputClass} />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-600">{suffix}</span>}
    </div>
  </div>
);

const Grid2 = ({ children }) => <div className="grid grid-cols-2 gap-2">{children}</div>;

// ─── INPUT PANEL ──────────────────────────────────────────────────────────────
const InputPanel = ({ cfg, setCfg, saveCfg, isSaving, perYearRatesActive, percentageCount, effectiveCfg }) => {
  const set = (key) => (val) => setCfg(p => ({ ...p, [key]: val }));
  const [openSec, setOpenSec] = useState({ general: true, assets: false, finance: false, other: false });
  const tog = (k) => setOpenSec(p => ({ ...p, [k]: !p[k] }));


  return (
    <div className="w-72 flex-shrink-0 bg-slate-950 border-r border-slate-800 overflow-y-auto text-xs">

      {/* PER-YEAR RATES BANNER */}
      {perYearRatesActive && (
        <div className="p-3 bg-gradient-to-r from-teal-950/80 to-cyan-950/60 border-b border-teal-500/30 flex items-center gap-2">
          <svg className="w-4 h-4 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
          <div>
            <p className="text-teal-300 font-black text-xs">Per-Year Rates Active</p>
            <p className="text-teal-500 text-xs">{percentageCount} custom FY rates override growth %</p>
          </div>
        </div>
      )}

      {/* KEY INPUT */}
      {!perYearRatesActive && (
        <div className="p-4 border-b bg-gradient-to-br from-teal-950/50 to-indigo-950/50 border-teal-500/20">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-black text-teal-400 uppercase tracking-widest flex items-center gap-1">
              ⚡ Growth Rate
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" value={cfg.growthPct === undefined ? '' : cfg.growthPct} step="0.5" min="0" max="50"
              onChange={e => setCfg(p => ({ ...p, growthPct: e.target.value }))}
              className="flex-1 bg-teal-950/50 border-2 border-teal-500/40 rounded-xl px-3 py-2 text-3xl font-black text-teal-300 font-mono text-center outline-none focus:border-teal-400 transition" />
            <span className="text-teal-400 font-bold">%</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">Change this ONE % → projects all your dynamic report items forward automatically.</p>
        </div>
      )}

      {/* GENERAL */}
      <div className="border-b border-slate-800">
        <SecHeader id="general" icon="🏥" label="General" openSec={openSec} tog={tog} />
        {openSec.general && (
          <div className="p-3">
            {perYearRatesActive ? (
              // When per-year rates are active, show auto-derived values (read-only)
              <div className="space-y-2">
                <div className="bg-teal-950/40 border border-teal-500/30 rounded-xl p-2.5">
                  <div className="text-xs text-teal-400 font-bold mb-1">⚡ Auto-derived from Project %</div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs">Start FY Year</span>
                    <span className="text-teal-300 font-black font-mono text-sm">{effectiveCfg?.startYear}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-slate-400 text-xs">No. of Years</span>
                    <span className="text-teal-300 font-black font-mono text-sm">{effectiveCfg?.numYears}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-slate-400 text-xs">Range</span>
                    <span className="text-teal-400 font-bold font-mono text-xs">FY {effectiveCfg?.startYear}-{String(effectiveCfg?.startYear + 1).slice(-2)} → {effectiveCfg?.startYear + effectiveCfg?.numYears - 1}-{String(effectiveCfg?.startYear + effectiveCfg?.numYears).slice(-2)}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Edit in Project Percentage page → change & re-generate records there</p>
                </div>
              </div>
            ) : (
              // Manual config when no per-year rates
              <>
                <Field label="Start FY Year" k="startYear" cfg={cfg} set={set} />
                <Field label="No. of Years" k="numYears" cfg={cfg} set={set} />
              </>
            )}
          </div>
        )}
      </div>



      <div className="p-4 mt-2">
        <button
          onClick={saveCfg}
          disabled={isSaving}
          className="w-full py-3 bg-violet-600/80 hover:bg-violet-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors shadow-lg shadow-violet-900/50 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Saving...
            </>
          ) : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
const BankFinancialProjection = ({ isEmbedded = false, reportId = null, reportName = '', tree = [], sectionTotal, onConfigLoad }) => {
  const [activeTab, setActiveTab] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Per-year growth rates from project_percentage_master
  const [percentageRecords, setPercentageRecords] = useState([]);
  const [perYearRatesActive, setPerYearRatesActive] = useState(false);

  const [cfg, setCfg] = useState({
    startYear: 2026, numYears: 15,
    growthPct: 10,
    bldCost: 1463.42, bldDepnPct: 10,
    eqCost: 565.88, eqDepnPct: 15,
    cmpCost: 71.08, cmpDepnPct: 25,
    furnCost: 50,
    capAmt: 600, tlAmt: 1500, odAmt: 1500,
    intRatePct: 9.5, repYrs: 9, morYrs: 1,
    taxPct: 30, mgmt1: 120, mgmtGrowPct: 20, leaseAdv: 60
  });

  // Fetch saved config and percentage records
  React.useEffect(() => {
    if (!reportId) {
      setIsLoading(false);
      setPercentageRecords([]);
      setPerYearRatesActive(false);
      return;
    }
    
    setIsLoading(true);

    const fetchConfig = axios.get(`${process.env.REACT_APP_API_URL}/api/financial-projection-config/${reportId}`)
      .then(res => {
        if (res.data) {
          let data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
          setCfg(prev => ({ ...prev, ...data }));
        }
      })
      .catch(err => {
        console.error("Failed to load projection config:", err);
      });

    const fetchPercentages = axios.get(`${process.env.REACT_APP_API_URL}/api/project-percentage`, { params: { report_id: reportId }, withCredentials: true })
      .then(res => {
        const records = res.data || [];
        setPercentageRecords(records);
        setPerYearRatesActive(records.length > 0);
      })
      .catch(() => { setPercentageRecords([]); setPerYearRatesActive(false); });
      
    Promise.all([fetchConfig, fetchPercentages]).finally(() => {
      setIsLoading(false);
    });
  }, [reportId]);

  // Save config to DB
  const saveCfg = async () => {
    if (!reportId) {
      toast.error("No project selected to save settings.");
      return;
    }
    try {
      setIsSaving(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/financial-projection-config/${reportId}`, cfg);
      toast.success("Projection settings saved!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  // Build growthRateMap keyed by FY year string — no index arithmetic, always matches
  const growthRateMap = useMemo(() => {
    if (!percentageRecords.length) return null;
    const map = {};
    percentageRecords.forEach(rec => {
      // rec.financial_year = '2028-29' → key = 'FY 2028-29'
      map[`FY ${rec.financial_year}`] = parseFloat(rec.percentage_value);
    });
    return map;
  }, [percentageRecords]);

  // When per-year rates are active, auto-derive startYear and numYears from the actual records.
  // This makes project-percentage the MASTER — no need to manually configure in FY Projection.
  const effectiveCfg = useMemo(() => {
    if (!percentageRecords.length) return cfg;
    const sorted = [...percentageRecords].sort((a, b) => a.financial_year.localeCompare(b.financial_year));
    const derivedStart = parseInt(sorted[0].financial_year.split('-')[0]);
    const derivedNum = sorted.length;
    return { ...cfg, startYear: derivedStart, numYears: derivedNum };
  }, [cfg, percentageRecords]);

  React.useEffect(() => {
    if (onConfigLoad) onConfigLoad(effectiveCfg);
  }, [effectiveCfg, onConfigLoad]);

  const d = useMemo(() => runCalc(effectiveCfg, tree, sectionTotal, growthRateMap), [effectiveCfg, tree, sectionTotal, growthRateMap]);

  const tabs = useMemo(() => {
    return (d.dynamicOtherTabs || []).map(t => ({ id: t.id, label: t.label }));
  }, [d]);

  React.useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 w-full bg-slate-950 rounded-2xl border border-slate-800">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-teal-400 font-medium text-sm animate-pulse">Loading Projections...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? '' : 'min-h-screen bg-slate-950'}>



      {/* Main Layout */}
      <div className="flex gap-0 border border-slate-800 rounded-2xl overflow-hidden">

        {/* INPUT PANEL */}
        <InputPanel cfg={cfg} setCfg={setCfg} saveCfg={saveCfg} isSaving={isSaving} perYearRatesActive={perYearRatesActive} percentageCount={percentageRecords.length} effectiveCfg={effectiveCfg} />

        {/* RIGHT CONTENT */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* Sub Tabs */}
          <div className="flex justify-between items-center bg-slate-900/50 border-b border-slate-800">
            <div className="flex gap-0 overflow-x-auto">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2.5 text-xs font-bold whitespace-nowrap transition border-b-2 ${activeTab === t.id
                    ? 'border-teal-500 text-teal-300 bg-teal-500/5'
                    : 'border-transparent text-slate-400 hover:text-white'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pr-4">
              <button
                onClick={() => {
                  const table = document.getElementById('export-table');
                  if (!table) return toast.error('Table not found');
                  const ws = XLSX.utils.table_to_sheet(table);
                  const range = XLSX.utils.decode_range(ws['!ref']);
                  for (let R = range.s.r; R <= range.e.r; ++R) {
                    for (let C = range.s.c; C <= range.e.c; ++C) {
                      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                      if (!ws[cellAddress]) continue;
                      if (R === 0) { // Header
                        ws[cellAddress].s = { font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF0F766E' } } };
                      }
                    }
                  }

                  // Auto-size columns
                  const colWidths = [{ wch: 40 }]; // Particulars column
                  for (let i = 0; i < 20; i++) colWidths.push({ wch: 15 }); // Year columns
                  ws['!cols'] = colWidths;

                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, activeTab.toUpperCase());
                  XLSX.writeFile(wb, `${reportName}_${activeTab}.xlsx`);
                  toast.success('Excel Exported!');
                }}
                className="bg-green-700/20 hover:bg-green-700/40 text-green-400 px-3 py-1.5 rounded-lg font-bold text-xs transition border border-green-700/30 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> XLS
              </button>
              <button
                onClick={() => {
                  const table = document.getElementById('export-table');
                  if (!table) return toast.error('Table not found');
                  const doc = new jsPDF('landscape', 'pt', 'a4');
                  doc.setFontSize(14);
                  const tabLabel = tabs.find(t => t.id === activeTab)?.label || 'Report';
                  const cleanLabel = tabLabel.replace(/[^\x20-\x7E]/g, "").trim(); // Remove emojis
                  doc.text(`${reportName} - ${cleanLabel}`, 40, 30);
                  autoTable(doc, {
                    html: '#export-table',
                    startY: 40,
                    theme: 'grid',
                    styles: { fontSize: 7, cellPadding: 3, textColor: [30, 41, 59], lineColor: [203, 213, 225] },
                    headStyles: { fillColor: [13, 148, 136], textColor: [255, 255, 255], fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                    didParseCell: function (data) {
                      if (data.section === 'body') {
                        const rowLabel = data.row.cells[0]?.text[0]?.toUpperCase() || '';

                        // Highlight Totals and Important Rows
                        if (rowLabel.includes('TOTAL') || rowLabel.includes('PROFIT') || rowLabel.includes('EBITDA') || rowLabel.includes('SURPLUS')) {
                          data.cell.styles.fontStyle = 'bold';
                          data.cell.styles.fillColor = [226, 232, 240];
                        }

                        // Color Numbers
                        if (data.column.index > 0) {
                          const txt = data.cell.text[0] || '';
                          const clean = txt.replace(/[₹,\s]/g, '');
                          const num = parseFloat(clean);
                          if (!isNaN(num) && txt !== '—' && txt !== '') {
                            if (num < 0) {
                              data.cell.styles.textColor = [225, 29, 72]; // rose-600
                              data.cell.styles.fontStyle = 'bold';
                            } else if (num > 0) {
                              data.cell.styles.textColor = [5, 150, 105]; // emerald-600
                              data.cell.styles.fontStyle = 'bold';
                            }
                          }
                        }
                      }
                    }
                  });
                  doc.save(`${reportName}_${activeTab}.pdf`);
                  toast.success('PDF Exported!');
                }}
                className="bg-rose-700/20 hover:bg-rose-700/40 text-rose-400 px-3 py-1.5 rounded-lg font-bold text-xs transition border border-rose-700/30 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> PDF
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto p-4">

            {/* ── DYNAMIC TABS ── */}
            {(d.dynamicOtherTabs || []).map(tab => activeTab === tab.id && (
              <TableWrap key={tab.id} title={`Projected ${tab.label}`}>
                <THead fy={d.fy} fyPerc={d.fyPerc} N={d.N} />
                <tbody>
                  {Object.keys(tab.categories).map(catName => {
                    const catItems = tab.categories[catName];
                    const catTotal = Array(d.N).fill(0);
                    catItems.forEach(item => {
                      item.values.forEach((v, i) => catTotal[i] += v);
                    });
                    return (
                      <React.Fragment key={catName}>
                        <SectionRow label={catName.toUpperCase()} cols={d.N} />
                        {catItems.map((item, i) => (
                          <DataRow key={i} label={item.name} values={item.values} sub />
                        ))}
                        <TotalRow label={`TOTAL ${catName.toUpperCase()}`} values={catTotal} color="text-slate-200" bg="bg-slate-800/30" />
                      </React.Fragment>
                    );
                  })}
                  <SectionRow label="SUMMARY" cols={d.N} />
                  <TotalRow label={`TOTAL ${tab.label.toUpperCase()}`} values={
                    Object.values(tab.categories).flat().reduce((acc, curr) => {
                      return acc.map((v, i) => v + curr.values[i]);
                    }, Array(d.N).fill(0))
                  } />
                </tbody>
              </TableWrap>
            ))}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BankFinancialProjection;
